/**
 * Adding, configuring and removing a view's colours.
 *
 * **Nothing here is optimistic**, which is the opposite of what a colour looks
 * like it should be: a decoration changes nothing about which rows there are or
 * what order they come in, so drawing the new colour immediately would cost
 * nothing to be wrong about. The reason it waits is the *configuration*. A
 * conditional colour holds conditions the backend edits behind the client's
 * back -- it drops one whose column has been deleted, and one whose filter type
 * the column's new type does not accept -- so the answer is not always what was
 * sent, and a client that assumed otherwise would show conditions the server
 * has already thrown away.
 *
 * The cached view is patched rather than invalidated, as everywhere else in
 * this file's neighbours: a decoration's answer says exactly what changed and
 * nothing else about the view did.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type {
  DecorationCreate,
  DecorationUpdate,
  DecorationUpdateBody,
  View,
  ViewDecoration,
} from '@/api/types'
import { withDecoration, withoutDecoration } from '../table/view-cache'

export interface DecorationMutations {
  add: (decoration: DecorationCreate) => void
  change: (decorationId: number, changes: DecorationUpdate) => void
  remove: (decorationId: number) => void
  /** Whether a change is on its way, so the controls can say so. */
  isPending: boolean
}

export function useViewDecorations(tableId: number, viewId: number): DecorationMutations {
  const queryClient = useQueryClient()
  const key = qk.views(tableId)

  const store = (decoration: ViewDecoration): void => {
    queryClient.setQueryData<View[]>(key, (views) =>
      views === undefined ? views : withDecoration(views, viewId, decoration),
    )
  }

  const add = useMutation({
    mutationFn: (body: DecorationCreate): Promise<ViewDecoration> =>
      unwrap(
        getApi().POST('/api/database/views/{view_id}/decorations/', {
          params: { path: { view_id: viewId } },
          body,
        }),
      ),
    onSuccess: store,
  })

  const change = useMutation({
    mutationFn: ({
      decorationId,
      changes,
    }: {
      decorationId: number
      changes: DecorationUpdate
    }): Promise<ViewDecoration> =>
      unwrap(
        getApi().PATCH('/api/database/views/decoration/{view_decoration_id}/', {
          params: { path: { view_decoration_id: decorationId } },
          // Cast because the spec is stricter than the endpoint about the
          // discriminator -- see `DecorationUpdate`, which says why.
          body: changes as DecorationUpdateBody,
        }),
      ),
    onSuccess: store,
  })

  const remove = useMutation({
    mutationFn: (decorationId: number): Promise<unknown> =>
      unwrap(
        getApi().DELETE('/api/database/views/decoration/{view_decoration_id}/', {
          params: { path: { view_decoration_id: decorationId } },
        }),
      ),
    // The answer is a 204 and says nothing, so what went is read from what was
    // asked rather than from the response.
    onSuccess: (_answer, decorationId) => {
      queryClient.setQueryData<View[]>(key, (views) =>
        views === undefined ? views : withoutDecoration(views, viewId, decorationId),
      )
    },
  })

  return {
    add: (decoration) => {
      add.mutate(decoration)
    },
    change: (decorationId, changes) => {
      change.mutate({ decorationId, changes })
    },
    remove: (decorationId) => {
      remove.mutate(decorationId)
    },
    isPending: add.isPending || change.isPending || remove.isPending,
  }
}
